import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { recordsAPI, inspectionsAPI, workshopsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn, formatDate, formatDateTime, getStatusColor, getRecordTypeLabel, getInspectionTypeLabel, downloadBlob } from '../lib/utils';
import {
  Search,
  Filter,
  Car,
  Truck,
  Bus,
  Package,
  Loader2,
  FileText,
  MoreHorizontal,
  Plus,
  Edit,
  Eye,
  ClipboardCheck,
  Download,
  QrCode,
  Cylinder,
  History,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Phone,
  ExternalLink,
  Paperclip,
  FileCheck,
  FolderOpen,
  RefreshCw,
  Upload,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const recordTypeIcons = {
  car: Car,
  '3w': Car,
  bus: Bus,
  truck: Truck,
  trailer: Truck,
  cng_storage: Package,
};

export default function RecordsListPage() {
  const { user, isAdmin, isInspector, isRegulator } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [recordType, setRecordType] = useState(searchParams.get('type') || 'all');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [expiring, setExpiring] = useState(searchParams.get('expiring') || 'all');
  const [cylinderStatus, setCylinderStatus] = useState(searchParams.get('cylinder_status') || '');
  const [expiryDays, setExpiryDays] = useState(searchParams.get('expiry_days') || '');

  // Detail drawer state
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedInspectionAttachments, setSelectedInspectionAttachments] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  
  // Replace report modal state
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // { inspectionId, reportType, reportName }
  const [replaceReason, setReplaceReason] = useState('');
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  
  // Delete confirmation state (Admin only - two-step process)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'record' | 'inspection', id, name }
  const [deleteStep, setDeleteStep] = useState(1); // 1 = initial, 2 = confirm
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Installer filter state (Admin/Regulator only)
  const [installerId, setInstallerId] = useState('');
  const [workshops, setWorkshops] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    loadData();
    if (isAdmin || user?.role === 'regulator') {
      loadWorkshops();
    }
  }, [recordType, status, expiring, cylinderStatus, expiryDays, installerId, page]);

  const loadWorkshops = async () => {
    try {
      const res = await workshopsAPI.getAll({ include_stats: true });
      setWorkshops(res.data || []);
    } catch (err) {
      console.error('Failed to load workshops');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: page * pageSize
      };
      if (search) params.search = search;
      if (recordType && recordType !== 'all') params.record_type = recordType;
      if (status && status !== 'all') params.status = status;
      if (expiring && expiring !== 'all') params.expiring = expiring;
      if (cylinderStatus) params.cylinder_status = cylinderStatus;
      if (expiryDays) params.expiry_days = parseInt(expiryDays);
      if (installerId && installerId !== 'all') params.installer_id = installerId;
      
      const [recordsRes, summaryRes] = await Promise.all([
        recordsAPI.getAll(params),
        recordsAPI.getSummary({ record_type: recordType !== 'all' ? recordType : null })
      ]);
      
      // Handle both old (array) and new (paginated object) response formats
      const recordsData = recordsRes.data;
      if (Array.isArray(recordsData)) {
        setRecords(recordsData);
        setTotal(recordsData.length);
      } else {
        setRecords(recordsData.records || []);
        setTotal(recordsData.total || 0);
      }
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0); // Reset to first page on search
    loadData();
  };

  const handleViewDetails = async (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
    setDetailLoading(true);
    
    try {
      // Lazy load full record details
      const fullRecordRes = await recordsAPI.getById(record.id);
      setSelectedRecord(fullRecordRes.data);
      
      const inspRes = await inspectionsAPI.getAll({ record_id: record.id });
      // Handle both response formats
      const inspData = inspRes.data;
      setInspections(Array.isArray(inspData) ? inspData : (inspData.inspections || []));
      
      if (isAdmin) {
        try {
          const auditRes = await recordsAPI.getAudit(record.id);
          setAuditLogs(auditRes.data);
        } catch (err) {
          console.log('Audit logs not available');
        }
      }
    } catch (err) {
      console.error('Failed to load details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadCertificate = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      downloadBlob(response.data, `certificate_${inspectionId}.pdf`);
      toast.success('Certificate downloaded');
    } catch (err) {
      toast.error('Failed to download certificate');
    }
  };

  const handleDownloadQR = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      downloadBlob(response.data, `qr_sticker_${inspectionId}.pdf`);
      toast.success('QR sticker downloaded');
    } catch (err) {
      toast.error('Failed to download QR sticker');
    }
  };

  const loadInspectionAttachments = async (inspectionId) => {
    setAttachmentsLoading(true);
    try {
      const response = await inspectionsAPI.getAllAttachments(inspectionId);
      setSelectedInspectionAttachments(response.data);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      if (err.response?.status === 403) {
        toast.error('Access denied to view attachments');
      } else {
        toast.error('Failed to load attachments');
      }
      setSelectedInspectionAttachments(null);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleDownloadTestReport = async (inspectionId, reportType, fileName) => {
    try {
      const response = await inspectionsAPI.getTestReport(inspectionId, reportType);
      let { data, name, type } = response.data;
      
      // Handle data URL format (data:mimetype;base64,actualdata)
      if (data && data.startsWith('data:')) {
        const parts = data.split(',');
        if (parts.length > 1) {
          data = parts[1]; // Get the actual base64 data after the comma
        }
      }
      
      // Convert base64 to blob
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: type || 'application/octet-stream' });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name || fileName || `${reportType}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (err) {
      console.error('Failed to download report:', err);
      toast.error('Failed to download report');
    }
  };

  // Open replace modal
  const handleOpenReplaceModal = (inspectionId, reportType, reportName) => {
    setReplaceTarget({ inspectionId, reportType, reportName });
    setReplaceReason('');
    setReplaceFile(null);
    setReplaceModalOpen(true);
  };

  // Handle file selection for replacement
  const handleReplaceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, JPEG, and PNG files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setReplaceFile(file);
    }
  };

  // Submit replacement
  const handleReplaceSubmit = async () => {
    if (!replaceFile) {
      toast.error('Please select a file to upload');
      return;
    }
    if (!replaceReason || replaceReason.trim().length < 10) {
      toast.error('Reason for modification must be at least 10 characters');
      return;
    }

    setReplacing(true);
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result;
          
          const payload = {
            report_type: replaceTarget.reportType,
            reason: replaceReason.trim(),
            new_file_name: replaceFile.name,
            new_file_type: replaceFile.type,
            new_file_size: replaceFile.size,
            new_file_data: base64Data
          };

          await inspectionsAPI.replaceTestReport(
            replaceTarget.inspectionId,
            replaceTarget.reportType,
            payload
          );

          toast.success('Test report replaced successfully. Change has been logged for audit.');
          setReplaceModalOpen(false);
          
          // Refresh attachments list
          loadInspectionAttachments(replaceTarget.inspectionId);
        } catch (err) {
          console.error('Failed to replace report:', err);
          toast.error(err.response?.data?.detail || 'Failed to replace test report');
        } finally {
          setReplacing(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setReplacing(false);
      };
      reader.readAsDataURL(replaceFile);
    } catch (err) {
      console.error('Failed to replace report:', err);
      toast.error('Failed to replace test report');
      setReplacing(false);
    }
  };

  // Delete handlers (Admin only - two-step confirmation)
  const handleOpenDeleteModal = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setDeleteStep(1);
    setDeleteReason('');
    setDeleteModalOpen(true);
  };

  const handleDeleteFirstStep = () => {
    // Move to confirmation step
    setDeleteStep(2);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteReason || deleteReason.trim().length < 10) {
      toast.error('Reason for deletion must be at least 10 characters');
      return;
    }

    setDeleting(true);
    try {
      if (deleteTarget.type === 'record') {
        await recordsAPI.delete(deleteTarget.id, deleteReason.trim());
        toast.success('Record and all related inspections deleted successfully');
        // Close drawer and refresh list
        setDrawerOpen(false);
        setSelectedRecord(null);
        loadData();
      } else if (deleteTarget.type === 'inspection') {
        await inspectionsAPI.delete(deleteTarget.id, deleteReason.trim());
        toast.success('Inspection deleted successfully');
        // Refresh inspections list in drawer
        const inspRes = await inspectionsAPI.getAll({ record_id: selectedRecord.id });
        setInspections(inspRes.data);
        // Refresh main list to update status
        loadData();
      }
      setDeleteModalOpen(false);
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    setDeleteReason('');
  };

  const getIdentifier = (record) => {
    return record.registration_number || record.storage_serial_number || 'N/A';
  };

  const getLatestStatus = (record) => {
    return record.latest_inspection?.status || 'none';
  };

  const getAssetType = (record) => {
    return record.record_type === 'cng_storage' ? 'CNG Storage' : 'Vehicle';
  };

  // Only Inspector can edit (Admin is view-only)
  const canEdit = isInspector;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="records-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Records</h1>
          <p className="text-muted-foreground mt-1">
            All vehicles and CNG storage facilities
          </p>
        </div>
        {(isAdmin || isInspector) && (
          <Button onClick={() => navigate('/records/new')} data-testid="new-record-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Record
          </Button>
        )}
      </div>

      {/* Asset Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Vehicles */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Car className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold">{summary.total_vehicles}</p>
                  <p className="text-xs text-muted-foreground">Total Vehicles</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2 text-xs">
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle className="h-3 w-3" /> {summary.vehicles_valid}
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-3 w-3" /> {summary.vehicles_expired}
                </span>
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock className="h-3 w-3" /> {summary.vehicles_expiring_soon}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Total CNG Storage */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold">{summary.total_storage}</p>
                  <p className="text-xs text-muted-foreground">CNG Storage</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2 text-xs">
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle className="h-3 w-3" /> {summary.storage_valid}
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-3 w-3" /> {summary.storage_expired}
                </span>
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock className="h-3 w-3" /> {summary.storage_expiring_soon}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Valid Certificates */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.vehicles_valid + summary.storage_valid}</p>
                  <p className="text-xs text-muted-foreground">Valid Certificates</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expiring Soon */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.vehicles_expiring_soon + summary.storage_expiring_soon}</p>
                  <p className="text-xs text-muted-foreground">Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          {/* Active Filter Banner for Expired Cylinders */}
          {cylinderStatus === 'expired' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Showing records with expired or expiring cylinders (within 30 days)
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setCylinderStatus('');
                  setSearchParams({});
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-100"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Clear Filter
              </Button>
            </div>
          )}
          
          {/* Active Filter Banner for Expiry Days */}
          {expiryDays && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Showing facilities with inspections expiring within {expiryDays} days
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setExpiryDays('');
                  setSearchParams({});
                }}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Clear Filter
              </Button>
            </div>
          )}
          
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by registration, serial, cylinder, or verification #..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger className="w-full md:w-40" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="car">🚗 Light Vehicle</SelectItem>
                <SelectItem value="3w">🛺 3-Wheeler</SelectItem>
                <SelectItem value="bus">🚌 Bus</SelectItem>
                <SelectItem value="truck">🛻 Truck</SelectItem>
                <SelectItem value="trailer">🚛 Trailer</SelectItem>
                <SelectItem value="cng_storage">🏭 CNG Storage</SelectItem>
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={expiring} onValueChange={setExpiring}>
              <SelectTrigger className="w-full md:w-40" data-testid="expiring-filter">
                <SelectValue placeholder="Expiring" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="7">Within 7 days</SelectItem>
                <SelectItem value="14">Within 14 days</SelectItem>
                <SelectItem value="30">Within 30 days</SelectItem>
              </SelectContent>
            </Select>

            {/* Installer Filter - Admin/Regulator only */}
            {(isAdmin || user?.role === 'regulator') && workshops.length > 0 && (
              <Select value={installerId} onValueChange={(v) => { setInstallerId(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-48" data-testid="installer-filter">
                  <SelectValue placeholder="All Installers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Installers</SelectItem>
                  {workshops.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} ({w.conversion_count || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button type="submit" data-testid="search-btn">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-medium text-lg mb-1">No records found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {search || recordType !== 'all' || status !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Records will appear here once inspections are created'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-[100px]">Asset Type</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead className="hidden md:table-cell">Verification #</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      {isAdmin ? 'Phone Number' : 'Owner Name'}
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Valid Until</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => {
                    const Icon = recordTypeIcons[record.record_type] || FileText;
                    const statusValue = getLatestStatus(record);
                    const isStorage = record.record_type === 'cng_storage';
                    
                    return (
                      <TableRow 
                        key={record.id} 
                        className="table-row-hover cursor-pointer"
                        onClick={() => handleViewDetails(record)}
                        data-testid={`record-row-${record.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "h-8 w-8 rounded-lg flex items-center justify-center",
                              isStorage ? "bg-purple-100" : "bg-blue-100"
                            )}>
                              <Icon className={cn(
                                "h-4 w-4",
                                isStorage ? "text-purple-600" : "text-blue-600"
                              )} />
                            </div>
                            <span className="text-xs text-muted-foreground hidden sm:inline">
                              {isStorage ? 'Storage' : 'Vehicle'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-mono font-medium">
                              {getIdentifier(record)}
                            </span>
                            <p className="text-xs text-muted-foreground">
                              {getRecordTypeLabel(record.record_type)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className="font-mono text-xs">
                            {record.latest_inspection?.verification_number || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {isAdmin ? (record.owner_phone || '-') : (record.owner_name || '-')}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={cn("capitalize", getStatusColor(statusValue))}
                          >
                            {statusValue === 'none' ? 'No Inspection' : statusValue}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {record.latest_inspection?.valid_to
                            ? formatDate(record.latest_inspection.valid_to)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`actions-${record.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewDetails(record)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                                {canEdit && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate(`/inspections/new?record_id=${record.id}`)}>
                                      <Plus className="mr-2 h-4 w-4" />
                                      New Inspection
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {record.latest_inspection?.has_certificate && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDownloadCertificate(record.latest_inspection.id)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      Download Certificate
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadQR(record.latest_inspection.id)}>
                                      <QrCode className="mr-2 h-4 w-4" />
                                      Download QR
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {isAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleOpenDeleteModal('record', record.id, getIdentifier(record))}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                                      data-testid={`delete-record-${record.id}`}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete Record
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
            
            {/* Pagination Controls - Always visible when not loading */}
            {!loading && records.length > 0 && (
              <div className="flex items-center justify-between py-4 px-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total} records
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {page + 1} of {Math.ceil(total / pageSize) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * pageSize >= total || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Record Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedRecord && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="flex items-center gap-2">
                  {React.createElement(recordTypeIcons[selectedRecord.record_type] || FileText, {
                    className: cn(
                      "h-5 w-5",
                      selectedRecord.record_type === 'cng_storage' ? "text-purple-600" : "text-blue-600"
                    )
                  })}
                  {getIdentifier(selectedRecord)}
                </SheetTitle>
                <SheetDescription asChild>
                  <div className="text-sm text-muted-foreground">
                    <Badge variant="outline" className={cn(
                      selectedRecord.record_type === 'cng_storage' 
                        ? "bg-purple-100 text-purple-700" 
                        : "bg-blue-100 text-blue-700"
                    )}>
                      {selectedRecord.record_type === 'cng_storage' ? 'CNG Storage' : 'Vehicle'}
                    </Badge>
                    <span className="ml-2">{getRecordTypeLabel(selectedRecord.record_type)}</span>
                    {selectedRecord.make && ` • ${selectedRecord.make} ${selectedRecord.model || ''}`}
                  </div>
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="w-full">
                <TabsList className={cn(
                  "w-full grid",
                  (isAdmin || isRegulator) ? "grid-cols-6" : "grid-cols-5"
                )}>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="cng">CNG</TabsTrigger>
                  <TabsTrigger value="cylinders">Cylinders</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                  {(isAdmin || isRegulator) && <TabsTrigger value="attachments">Attachments</TabsTrigger>}
                  {isAdmin && <TabsTrigger value="audit">Audit</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="space-y-4 mt-4">
                  {/* Status Card */}
                  {selectedRecord.latest_inspection && (
                    <div className={cn(
                      "p-4 rounded-lg border-l-4",
                      selectedRecord.latest_inspection.status === 'valid' 
                        ? "bg-emerald-50 border-l-emerald-500" 
                        : selectedRecord.latest_inspection.status === 'expired'
                        ? "bg-red-50 border-l-red-500"
                        : "bg-amber-50 border-l-amber-500"
                    )}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Inspection Status</p>
                          <Badge 
                            variant="outline"
                            className={cn("mt-1 capitalize", getStatusColor(selectedRecord.latest_inspection.status))}
                          >
                            {selectedRecord.latest_inspection.status}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Valid Until</p>
                          <p className="text-sm font-medium mt-1">
                            {formatDate(selectedRecord.latest_inspection.valid_to)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">Verification #</p>
                        <p className="text-sm font-mono font-medium">
                          {selectedRecord.latest_inspection.verification_number}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Record Details */}
                  <div className="grid grid-cols-2 gap-3">
                    {selectedRecord.registration_number && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Registration</p>
                        <p className="text-sm font-mono font-medium mt-1">
                          {selectedRecord.registration_number}
                        </p>
                      </div>
                    )}
                    {selectedRecord.storage_serial_number && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Storage Serial</p>
                        <p className="text-sm font-mono font-medium mt-1">
                          {selectedRecord.storage_serial_number}
                        </p>
                      </div>
                    )}
                    {selectedRecord.year && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Year</p>
                        <p className="text-sm font-medium mt-1">{selectedRecord.year}</p>
                      </div>
                    )}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Inspections</p>
                      <p className="text-sm font-medium mt-1">{selectedRecord.inspection_count || 0}</p>
                    </div>
                  </div>

                  {/* Contact Info - Role-based visibility */}
                  {/* Admin sees Phone Number, Inspector/Regulator sees Owner Name */}
                  {(isAdmin && selectedRecord.owner_phone) || (!isAdmin && selectedRecord.owner_name) ? (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          {isAdmin ? 'Contact Information' : 'Owner Information'}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {isAdmin ? (
                            // Admin sees Phone Number only
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-xs text-muted-foreground">Phone Number</p>
                              <p className="text-sm font-medium mt-1 font-mono">{selectedRecord.owner_phone}</p>
                            </div>
                          ) : (
                            // Inspector/Regulator sees Owner Name only
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-xs text-muted-foreground">Owner Name</p>
                              <p className="text-sm font-medium mt-1">{selectedRecord.owner_name}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}

                  {/* Quick Actions - Only for Inspector */}
                  {canEdit && (
                    <>
                      <Separator />
                      <div className="flex gap-2">
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => navigate(`/inspections/new?record_id=${selectedRecord.id}`)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          New Inspection
                        </Button>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="cng" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">NGV Origin</p>
                      <p className="text-sm font-medium mt-1 capitalize">
                        {selectedRecord.ngv_origin?.replace('_', ' ') || '-'}
                      </p>
                    </div>
                    
                    {selectedRecord.ngv_origin === 'retrofitted' && (
                      <>
                        {selectedRecord.conversion_kit_manufacturer && (
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Kit Manufacturer</p>
                            <p className="text-sm font-medium mt-1">
                              {selectedRecord.conversion_kit_manufacturer}
                            </p>
                          </div>
                        )}
                        {selectedRecord.conversion_kit_model && (
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Kit Model</p>
                            <p className="text-sm font-medium mt-1">
                              {selectedRecord.conversion_kit_model}
                            </p>
                          </div>
                        )}
                        {selectedRecord.installer_name && (
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground">Installer</p>
                            <p className="text-sm font-medium mt-1">
                              {selectedRecord.installer_name}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="cylinders" className="mt-4">
                  {selectedRecord.cylinders?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Cylinder className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No cylinders recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedRecord.cylinders?.map((cyl, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono font-medium text-sm">
                              {cyl.serial_number}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              #{idx + 1}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Manufacturer:</span>
                              <span className="ml-1">{cyl.manufacturer || '-'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Capacity:</span>
                              <span className="ml-1">{cyl.capacity ? `${cyl.capacity}L` : '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : inspections.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No inspections yet</p>
                      {canEdit && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4"
                          onClick={() => navigate(`/inspections/new?record_id=${selectedRecord.id}`)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Create Inspection
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {inspections.map((insp) => (
                        <div key={insp.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-mono text-xs">{insp.verification_number}</span>
                              <p className="text-sm font-medium capitalize mt-0.5">
                                {getInspectionTypeLabel(insp.inspection_type)}
                              </p>
                            </div>
                            <Badge 
                              variant="outline"
                              className={cn("capitalize", getStatusColor(insp.status))}
                            >
                              {insp.status}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{formatDate(insp.created_at)}</span>
                            <div className="flex gap-1">
                              {/* View live verification page */}
                              {insp.verification_token && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => window.open(`/verify/${insp.verification_token}`, '_blank')}
                                  title="View Live Verification Page"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              )}
                              {insp.has_certificate && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleDownloadCertificate(insp.id)}
                                    title="Download Certificate"
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleDownloadQR(insp.id)}
                                    title="Download QR Sticker"
                                  >
                                    <QrCode className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                                  onClick={() => handleOpenDeleteModal('inspection', insp.id, insp.verification_number)}
                                  title="Delete Inspection"
                                  data-testid={`delete-inspection-${insp.id}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {(isAdmin || isRegulator) && (
                  <TabsContent value="attachments" className="mt-4">
                    {/* Select Inspection Dropdown */}
                    {inspections.length > 0 ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground">Select Inspection to View Attachments</label>
                          <Select 
                            onValueChange={(value) => loadInspectionAttachments(value)}
                            data-testid="select-inspection-attachments"
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an inspection..." />
                            </SelectTrigger>
                            <SelectContent>
                              {inspections.map((insp) => (
                                <SelectItem key={insp.id} value={insp.id}>
                                  {insp.verification_number} - {formatDate(insp.created_at)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Attachments List */}
                        {attachmentsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          </div>
                        ) : selectedInspectionAttachments ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>Verification: {selectedInspectionAttachments.verification_number}</span>
                              <span>{selectedInspectionAttachments.total_count} document(s)</span>
                            </div>
                            
                            {selectedInspectionAttachments.attachments?.length === 0 ? (
                              <div className="text-center py-6 text-muted-foreground">
                                <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No attachments available</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {selectedInspectionAttachments.attachments?.map((att) => (
                                  <div 
                                    key={att.id} 
                                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                                    data-testid={`attachment-${att.type}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={cn(
                                        "h-9 w-9 rounded-lg flex items-center justify-center",
                                        att.type === 'certificate' ? "bg-blue-100" :
                                        att.type === 'qr_sticker' ? "bg-purple-100" :
                                        att.type === 'test_report' ? "bg-emerald-100" :
                                        "bg-gray-100"
                                      )}>
                                        {att.type === 'certificate' ? (
                                          <FileText className="h-4 w-4 text-blue-600" />
                                        ) : att.type === 'qr_sticker' ? (
                                          <QrCode className="h-4 w-4 text-purple-600" />
                                        ) : att.type === 'test_report' ? (
                                          <FileCheck className="h-4 w-4 text-emerald-600" />
                                        ) : (
                                          <Paperclip className="h-4 w-4 text-gray-600" />
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium">{att.name}</p>
                                        <p className="text-xs text-muted-foreground">{att.description}</p>
                                        {att.file_size && (
                                          <p className="text-xs text-muted-foreground">
                                            {(att.file_size / 1024).toFixed(1)} KB
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          if (att.type === 'certificate') {
                                            handleDownloadCertificate(selectedInspectionAttachments.inspection_id);
                                          } else if (att.type === 'qr_sticker') {
                                            handleDownloadQR(selectedInspectionAttachments.inspection_id);
                                          } else if (att.type === 'test_report') {
                                            handleDownloadTestReport(
                                              selectedInspectionAttachments.inspection_id, 
                                              att.report_type,
                                              att.original_name
                                            );
                                          }
                                        }}
                                        data-testid={`download-${att.type}`}
                                        title="Download"
                                      >
                                        <Download className="h-4 w-4" />
                                      </Button>
                                      {att.type === 'test_report' && (isAdmin || isRegulator) && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleOpenReplaceModal(
                                            selectedInspectionAttachments.inspection_id,
                                            att.report_type,
                                            att.name
                                          )}
                                          data-testid={`replace-${att.report_type}`}
                                          title="Replace with new file"
                                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                        >
                                          <RefreshCw className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">Select an inspection to view its certificates and attachments</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No inspections available</p>
                      </div>
                    )}
                  </TabsContent>
                )}

                {isAdmin && (
                  <TabsContent value="audit" className="mt-4">
                    {detailLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : auditLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No audit history</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {auditLogs.map((log, idx) => (
                          <div key={idx} className="p-3 border rounded-lg text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium capitalize">
                                {log.action.replace(/_/g, ' ')}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(log.timestamp)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              by {log.user_name || log.user_email}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                )}
              </Tabs>

              <Separator className="my-4" />

              <div className="text-xs text-muted-foreground space-y-1">
                <p>Created: {formatDateTime(selectedRecord.created_at)}</p>
                <p>ID: {selectedRecord.id}</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Replace Test Report Modal */}
      <Dialog open={replaceModalOpen} onOpenChange={setReplaceModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Replace Test Report
            </DialogTitle>
            <DialogDescription>
              Replace &ldquo;{replaceTarget?.reportName}&rdquo; with a new file. This action will be logged for audit purposes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current file info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Current File</p>
              <p className="text-sm font-medium">{replaceTarget?.reportName}</p>
            </div>

            {/* New file upload */}
            <div className="space-y-2">
              <Label htmlFor="new-file" className="flex items-center gap-1">
                New File *
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="new-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleReplaceFileChange}
                  className="flex-1"
                  data-testid="replace-file-input"
                />
              </div>
              {replaceFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: {replaceFile.name} ({(replaceFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Allowed formats: PDF, JPEG, PNG (max 10MB)
              </p>
            </div>

            {/* Reason for modification (mandatory) */}
            <div className="space-y-2">
              <Label htmlFor="replace-reason" className="flex items-center gap-1">
                Reason for Modification *
                <span className="text-xs text-muted-foreground">(min 10 characters)</span>
              </Label>
              <Textarea
                id="replace-reason"
                placeholder="Please provide a detailed reason for replacing this test report (e.g., 'Original report contained incorrect test date', 'Updated results after re-testing', etc.)"
                value={replaceReason}
                onChange={(e) => setReplaceReason(e.target.value)}
                rows={3}
                className="resize-none"
                data-testid="replace-reason-input"
              />
              <p className="text-xs text-muted-foreground">
                {replaceReason.length}/10 characters minimum
              </p>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <p className="font-medium">Important Notice</p>
                <p>This change will be permanently logged with your name, timestamp, and the reason provided. The previous file version will be archived in the audit trail.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReplaceModalOpen(false)}
              disabled={replacing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReplaceSubmit}
              disabled={replacing || !replaceFile || replaceReason.trim().length < 10}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="replace-submit-btn"
            >
              {replacing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Replacing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Replace Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal (Admin Only - Two-Step) */}
      <Dialog open={deleteModalOpen} onOpenChange={handleCloseDeleteModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {deleteStep === 1 ? 'Delete' : 'Confirm Delete'} {deleteTarget?.type === 'record' ? 'Record' : 'Inspection'}
            </DialogTitle>
            <DialogDescription>
              {deleteStep === 1 ? (
                <>
                  You are about to delete <strong>{deleteTarget?.name}</strong>.
                  {deleteTarget?.type === 'record' && ' This will also delete all related inspections and certificates.'}
                </>
              ) : (
                <>
                  <span className="text-red-600 font-medium">Final confirmation required.</span> This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {deleteStep === 1 ? (
              <>
                {/* Warning message for step 1 */}
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/50 dark:border-red-800">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800 dark:text-red-200">Warning</p>
                    <p className="text-red-700 dark:text-red-300">
                      {deleteTarget?.type === 'record' 
                        ? 'Deleting this record will permanently remove the vehicle/storage entry and ALL associated inspections, certificates, and attachments.'
                        : 'Deleting this inspection will permanently remove the inspection record, its certificate, and QR code.'}
                    </p>
                  </div>
                </div>

                {/* What will be deleted */}
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Item to be deleted:</p>
                  <div className="flex items-center gap-2">
                    {deleteTarget?.type === 'record' ? (
                      <Car className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <FileCheck className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-mono font-medium">{deleteTarget?.name}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Step 2 - Require reason */}
                <div className="space-y-2">
                  <Label htmlFor="delete-reason" className="flex items-center gap-1">
                    Reason for Deletion *
                    <span className="text-xs text-muted-foreground">(min 10 characters)</span>
                  </Label>
                  <Textarea
                    id="delete-reason"
                    placeholder="Please provide a detailed reason for this deletion (e.g., 'Duplicate entry created in error', 'Test record no longer needed', etc.)"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    rows={3}
                    className="resize-none"
                    data-testid="delete-reason-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {deleteReason.length}/10 characters minimum
                  </p>
                </div>

                {/* Final warning */}
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/50 dark:border-red-800">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800 dark:text-red-200">This action is irreversible</p>
                    <p className="text-red-700 dark:text-red-300">
                      Once deleted, this data cannot be recovered. This action will be logged in the audit trail.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDeleteModal}
              disabled={deleting}
            >
              Cancel
            </Button>
            {deleteStep === 1 ? (
              <Button
                variant="destructive"
                onClick={handleDeleteFirstStep}
                data-testid="delete-first-step-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting || deleteReason.trim().length < 10}
                data-testid="delete-confirm-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Confirm Delete
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
